import { useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemAvatar,
  ListItemText,
  ListItem,
  List,
  Divider,
  Box,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { CheckCircle, Error, Warning, ExpandMore } from '@mui/icons-material';
import { groupListByProperty, unique } from '../../../utils/collections';
import DialogBase from './DialogBase';

const FAILED = 'FAILED';
const PARTIAL = 'PARTIAL';
const SUCCESS = 'SUCCESS';

function getEquipmentIcon(status) {
  switch (status) {
    case SUCCESS:
      return <CheckCircle color="success" />;
    case FAILED:
      return <Error color="error" />;
    default:
      return <Warning color="warning" />;
  }
}

function getPointIcon(isError) {
  if (isError) return <Error color="error" />;
  return <CheckCircle color="success" />;
}

const PublishDialog = ({
  onExit,
  equipment,

  publishEquipment,
}) => {
  const [results, setResults] = useState(null);
  return (
    <>
      <Dialog open={results} fullWidth maxWidth="md">
        <DialogTitle>Publish Results</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography component="div" sx={{ fontWeight: 'bold' }}>
              {results?.[FAILED]?.length ?? 0} Failed Publish
              {results?.[FAILED]?.length !== 1 ? 'es' : ''}
            </Typography>
            <Typography component="div" sx={{ fontWeight: 'bold' }}>
              {results?.[PARTIAL]?.length ?? 0} Partial Publish
              {results?.[PARTIAL]?.length !== 1 ? 'es' : ''}
            </Typography>
            <Typography component="div" sx={{ fontWeight: 'bold' }}>
              {results?.[SUCCESS]?.length ?? 0} Succeeded Publish
              {results?.[SUCCESS]?.length !== 1 ? 'es' : ''}
            </Typography>
          </Box>
          <Box style={{ overflowY: 'auto', maxHeight: '300px', height: '300px' }}>
            {[FAILED, PARTIAL, SUCCESS].map((eStatus) =>
              results?.[eStatus]?.map(({ equip, equipMessages, pointsMessages }) => (
                <Accordion
                  key={equip.equipId}
                  border="1"
                  disableGutters
                  sx={{ overflow: 'hidden' }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                    >
                      {getEquipmentIcon(eStatus)}
                      <Typography>{equip.name}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List sx={{ width: '100%' }}>
                      <ListItem alignItems="flex-start" key={equip.equipId}>
                        <ListItemAvatar>{getEquipmentIcon(eStatus)}</ListItemAvatar>
                        <ListItemText
                          primary={equip.name}
                          secondary={equipMessages.map((v) => v.message)}
                        />
                        <Divider variant="inset" />
                      </ListItem>
                      {Object.entries(pointsMessages).map(([topic, pointResults]) => (
                        <ListItem alignItems="flex-start" key={[topic, equip.equipId].join('-')}>
                          <ListItemAvatar>{getPointIcon(pointResults.length > 0)}</ListItemAvatar>
                          <ListItemText
                            primary={topic}
                            secondary={pointResults.map((v) => v.message)}
                          />
                          <Divider variant="inset" />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setResults(null);
            onExit();
          }}
          id="confirm"
        >
          <DialogActions>
            <Button variant="contained" color="success" type="submit" form="confirm">
              Confirm
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={equipment.length > 0} maxWidth="md">
        <DialogBase
          onExit={onExit}
          submit={() =>
            publishEquipment(equipment).then((result) => {
              setResults(
                groupListByProperty(
                  'eStatus',
                  equipment.map((e) => {
                    const eMsgs = result.equipment[e.equipId];
                    const pointsMsgs = Object.fromEntries(
                      e.points
                        .filter((p) => result.points[p.topic])
                        .map((p) => [p.topic, result.points[p.topic]])
                    );
                    let eStatus;
                    if (eMsgs.length > 0) eStatus = FAILED;
                    else if (Object.values(pointsMsgs).some((pointMsgs) => pointMsgs.length > 0))
                      eStatus = PARTIAL;
                    else eStatus = SUCCESS;
                    return { eStatus, equip: e, equipMessages: eMsgs, pointsMessages: pointsMsgs };
                  })
                )
              );
            })
          }
          id="publish"
        >
          {equipment.length} equipment /{' '}
          {
            unique(
              equipment.flatMap((e) => e.points),
              (p) => p.topic
            ).length
          }{' '}
          points will be published
        </DialogBase>
      </Dialog>
    </>
  );
};

export default PublishDialog;
